import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  Button,
  Link as ChakraLink,
  Spacer,
  HStack,
  IconButton,
  useDisclosure,
  VStack,
  Collapse,
  useColorModeValue,
  Container,
  Image,
} from '@chakra-ui/react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { FiHome, FiUsers, FiDollarSign, FiBook, FiInfo, FiBarChart2 } from 'react-icons/fi';

const getNavItems = (user) => {
  const baseItems = [
    { label: 'Home', icon: FiHome, href: '/' },
    { label: 'Funds', icon: FiBarChart2, href: '/funds' },
    { label: 'Blog', icon: FiBook, href: '/blog' },
    { label: 'About', icon: FiInfo, href: '/about' },
    { label: 'Calculator', href: '/calculator' },
    { label: 'Products', href: '/products' },
  ];

  if (!user) return baseItems;

  const authItems = [
    ...(user.role === 'admin' ? [
      { label: 'Admin Dashboard', icon: FiUsers, href: '/admin' },
    ] : []),
    ...(['agent', 'sub-agent'].includes(user.role) && user.isApproved ? [
      { label: 'Agent Dashboard', icon: FiUsers, href: '/agent' },
      { label: 'New Lead', icon: FiUsers, href: '/lead/new' },
      { label: 'Payouts', icon: FiDollarSign, href: '/payouts' },
    ] : []),
  ];

  return [...baseItems, ...authItems];
};

const NavLink = ({ to, children, isMobile }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  const color = useColorModeValue('gray.700', 'white');
  const activeColor = 'brand.500';
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  return (
    <ChakraLink
      as={Link}
      to={to}
      px={3}
      py={2}
      rounded="md"
      color={isActive ? activeColor : color}
      fontWeight={isActive ? "semibold" : "medium"}
      _hover={{
        textDecoration: 'none',
        bg: hoverBg,
        color: activeColor,
      }}
      w={isMobile ? "full" : "auto"}
      textAlign={isMobile ? "center" : "left"}
    >
      {children}
    </ChakraLink>
  );
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isOpen, onToggle } = useDisclosure();
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    // Remove or comment out the console.log
    // console.log('Current user in Navbar:', user);
  }, [user]);

  const navItems = getNavItems(user);

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={1000}
      bg={bg}
      borderBottom="1px"
      borderColor={borderColor}
      shadow="sm"
    >
      <Container maxW="7xl">
        <Flex minH="16" align="center" justify="space-between">
          {/* Logo */}
          <ChakraLink
            as={Link}
            to="/"
            display="flex"
            alignItems="center"
            _hover={{ textDecoration: 'none' }}
          >
            <Image
              src="/images/logo.svg"
              alt="Budget Brilliance"
              h="40px"
              mr={3}
            />
          </ChakraLink>

          {/* Desktop Navigation */}
          <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
            {navItems.map((item) => (
              <NavLink key={item.href} to={item.href}>
                {item.label}
              </NavLink>
            ))}
          </HStack>

          {/* Auth Buttons or User Menu */}
          <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
            {user ? (
              <Button
                colorScheme="brand"
                variant="outline"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
              >
                Logout
              </Button>
            ) : (
              <>
                <Button
                  as={Link}
                  to="/login"
                  variant="ghost"
                  colorScheme="brand"
                >
                  Login
                </Button>
                <Button
                  as={Link}
                  to="/register"
                  colorScheme="brand"
                >
                  Register
                </Button>
              </>
            )}
          </HStack>

          {/* Mobile menu button */}
          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onToggle}
            icon={isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant="ghost"
            aria-label="Toggle Navigation"
          />
        </Flex>

        {/* Mobile Navigation */}
        <Collapse in={isOpen} animateOpacity>
          <VStack
            display={{ base: 'flex', md: 'none' }}
            py={4}
            spacing={2}
          >
            {navItems.map((item) => (
              <NavLink key={item.href} to={item.href} isMobile>
                {item.label}
              </NavLink>
            ))}
            {!user && (
              <>
                <Button
                  as={Link}
                  to="/login"
                  variant="ghost"
                  colorScheme="brand"
                  w="full"
                >
                  Login
                </Button>
                <Button
                  as={Link}
                  to="/register"
                  colorScheme="brand"
                  w="full"
                >
                  Register
                </Button>
              </>
            )}
            {user && (
              <Button
                w="full"
                colorScheme="brand"
                variant="outline"
                onClick={() => {
                  logout();
                  navigate('/');
                }}
              >
                Logout
              </Button>
            )}
          </VStack>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Navbar;
